import { useEffect, useState } from "react";
import {
    createDeliverySlot,
    deleteDeliverySlot,
    fetchDeliveryLocations,
    getAllDeliverySlots,
    toggleDeliverySlotActive,
    updateDeliverySlot,
} from "../service/api/orders";

/* ─── Brand tokens (matches your dashboard) ─────────────────────────── */
const B = {
    orange:     "#ff6b00",
    orangeLight:"#fff5ee",
    orangeMid:  "#ff914d",
    orangeText: "#c24e00",
    green:      "#10b981",
    greenLight: "#f0fdf4",
    red:        "#ef4444",
    redLight:   "#fff5f5",
    blue:       "#3686ec",
    blueLight:  "#eef6ff",
    gray50:     "#f8fafc",
    gray100:    "#f1f5f9",
    gray200:    "#edf2f7",
    gray300:    "#e2e8f0",
    gray400:    "#cbd5e1",
    gray500:    "#94a3b8",
    gray600:    "#64748b",
    gray700:    "#475569",
    gray800:    "#1e293b",
    gray900:    "#0f172a",
    white:      "#ffffff",
    shadow:     "0 2px 8px rgba(0,0,0,0.09)",
    shadowMd:   "0 4px 12px rgba(0,0,0,0.07)",
    shadowLg:   "0 8px 24px rgba(0,0,0,0.12)",
    font:       "'Inter', 'Segoe UI', sans-serif",
};

/* ─── Helpers ────────────────────────────────────────────────────────── */
const fmt12 = (t) => {
    if (!t) return "—";
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
        timeZone: "Asia/Kolkata", hour12: false,
    });
};

function useIsMobile(bp = 768) {
    const [m, setM] = useState(() => window.innerWidth < bp);
    useEffect(() => {
        const h = () => setM(window.innerWidth < bp);
        window.addEventListener("resize", h);
        return () => window.removeEventListener("resize", h);
    }, [bp]);
    return m;
}

/* ─── Icons ──────────────────────────────────────────────────────────── */
const Icons = {
    Plus: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
    ),
    Settings: () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
    ),
    Trash: () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
        </svg>
    ),
    Close: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    ),
    Clock: () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
    ),
    Location: () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/>
        </svg>
    ),
    EmptyBox: () => (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
    ),
    Slots: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
    ),
};

/* ─── Primitives ─────────────────────────────────────────────────────── */
function Btn({ children, onClick, variant = "primary", small, disabled, fullWidth, style: sx }) {
    const base = {
        border: "none", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 700, fontSize: small ? 12 : 13,
        padding: small ? "5px 11px" : "9px 20px",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        opacity: disabled ? 0.55 : 1, width: fullWidth ? "100%" : undefined,
        fontFamily: B.font, transition: "all .15s", letterSpacing: "0.01em", ...sx,
    };
    const variants = {
        primary:  { background: B.orange, color: B.white, boxShadow: "0 2px 8px rgba(255,107,0,.25)" },
        success:  { background: B.green,  color: B.white, boxShadow: "0 2px 8px rgba(16,185,129,.2)" },
        danger:   { background: B.redLight, color: B.red, border: `1px solid rgba(239,68,68,.25)` },
        ghost:    { background: B.gray100, color: B.gray700, border: `1px solid ${B.gray300}` },
    };
    return (
        <button
            style={{ ...base, ...variants[variant] }}
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={e => { if (!disabled) e.currentTarget.style.opacity = "1"; }}
        >
            {children}
        </button>
    );
}

function Input({ label, ...props }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {label && <label style={{ fontSize: 11, fontWeight: 700, color: B.gray600, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: B.font }}>{label}</label>}
            <input {...props} style={{
                border: `1px solid ${B.gray300}`, borderRadius: 8, padding: "9px 12px",
                fontSize: 13, color: B.gray800, outline: "none", background: B.white,
                width: "100%", boxSizing: "border-box", fontFamily: B.font,
                transition: "border-color .15s, box-shadow .15s", ...props.style,
            }}
                   onFocus={e => { e.target.style.borderColor = B.orange; e.target.style.boxShadow = `0 0 0 3px rgba(255,107,0,.1)`; }}
                   onBlur={e => { e.target.style.borderColor = B.gray300; e.target.style.boxShadow = "none"; }}
            />
        </div>
    );
}

function Select({ label, children, ...props }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {label && <label style={{ fontSize: 11, fontWeight: 700, color: B.gray600, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: B.font }}>{label}</label>}
            <select {...props} style={{
                border: `1px solid ${B.gray300}`, borderRadius: 8, padding: "9px 12px",
                fontSize: 13, color: B.gray800, outline: "none", background: B.white,
                width: "100%", boxSizing: "border-box", fontFamily: B.font,
                transition: "border-color .15s", ...props.style,
            }}
                    onFocus={e => { e.target.style.borderColor = B.orange; }}
                    onBlur={e => { e.target.style.borderColor = B.gray300; }}
            >
                {children}
            </select>
        </div>
    );
}

/* ─── Active Toggle ──────────────────────────────────────────────────── */
function ActiveToggle({ active, disabled, onToggle }) {
    return (
        <button onClick={onToggle} disabled={disabled} title={active ? "Active" : "Inactive"}
                style={{
                    width: 46, height: 24, borderRadius: 999, border: "none",
                    background: active ? B.orange : B.gray400,
                    cursor: disabled ? "not-allowed" : "pointer",
                    position: "relative", transition: "background .2s",
                    opacity: disabled ? 0.7 : 1, flexShrink: 0,
                }}
        >
            <span style={{
                position: "absolute", top: 3, left: active ? 24 : 3,
                width: 18, height: 18, borderRadius: "50%", background: B.white,
                transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)", display: "block",
            }} />
        </button>
    );
}

/* ─── Skeleton ───────────────────────────────────────────────────────── */
function Skeleton({ w = "100%", h = 13, r = 6 }) {
    return (
        <div style={{
            width: w, height: h, borderRadius: r,
            background: "#f3f4f6", backgroundSize: "200% 100%",
            animation: "ds-shimmer 1.4s infinite",
        }} />
    );
}

function SkeletonRow({ cols = 9 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i} style={{ padding: "16px 16px" }}>
                    <Skeleton w={i === cols - 1 ? 110 : `${50 + (i * 19) % 40}%`} />
                </td>
            ))}
        </tr>
    );
}

/* ─── Status Badge (matches your .badge / .om-status-badge) ─────────── */
function StatusBadge({ active }) {
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "4px 10px", borderRadius: 20,
            fontSize: 12, fontWeight: 700, fontFamily: B.font,
            background: active ? "#fff5ee" : B.gray100,
            color: active ? B.orange : B.gray600,
            border: `1px solid ${active ? "rgba(255,107,0,.25)" : B.gray300}`,
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? B.orange : B.gray400, display: "inline-block" }} />
            {active ? "Active" : "Inactive"}
        </span>
    );
}

/* ─── TimeBox (card view) ────────────────────────────────────────────── */
function TimeBox({ label, value }) {
    return (
        <div style={{
            border: `1px solid ${B.gray200}`, borderRadius: 8,
            padding: "8px 10px", background: B.gray50,
        }}>
            <div style={{ fontSize: 10, color: B.gray500, fontWeight: 700, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: B.font }}>{label}</div>
            <div style={{ fontSize: 13, color: B.gray800, fontWeight: 700, display: "flex", alignItems: "center", gap: 5, fontFamily: B.font }}>
                <span style={{ color: B.orange }}><Icons.Clock /></span>
                {fmt12(value)}
            </div>
        </div>
    );
}

/* ─── Mobile Card ────────────────────────────────────────────────────── */
function DeliverySlotCard({ slot, onEdit, onDelete, onToggle, toggling }) {
    const locationName = slot.delivery_location?.name || slot.deliveryLocation?.name || slot.location?.name || "—";
    return (
        <div style={{
            background: B.white, borderRadius: 12,
            border: `1px solid ${B.gray200}`,
            boxShadow: B.shadow, padding: 16,
            display: "flex", flexDirection: "column", gap: 10,
            transition: "transform .2s, box-shadow .2s",
        }}
             onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = B.shadowMd; }}
             onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = B.shadow; }}
        >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: B.gray900, fontFamily: B.font }}>{slot.name}</div>
                    <div style={{ fontSize: 12, color: B.gray500, display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontFamily: B.font }}>
                        <span style={{ color: B.orange }}><Icons.Location /></span>
                        {locationName}
                    </div>
                </div>
                <ActiveToggle active={Boolean(slot.is_active)} disabled={toggling} onToggle={onToggle} />
            </div>

            {/* Time grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <TimeBox label="Visible" value={slot.visible_at} />
                <TimeBox label="Cut Off" value={slot.cut_off_at} />
                <TimeBox label="Start" value={slot.start_time} />
                <TimeBox label="End" value={slot.end_time} />
            </div>

            {/* Dates */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: B.gray400, fontFamily: B.font }}>Updated: {formatDateTime(slot.updated_at)}</span>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, paddingTop: 10, borderTop: `1px solid ${B.gray100}` }}>
                <Btn small variant="ghost" onClick={onEdit}><Icons.Settings /> Edit</Btn>
                <Btn small variant="danger" onClick={onDelete}><Icons.Trash /> Delete</Btn>
            </div>
        </div>
    );
}

/* ─── Modal ──────────────────────────────────────────────────────────── */
function Modal({ open, onClose, title, children }) {
    const isMobile = useIsMobile();
    if (!open) return null;

    const overlayStyle = isMobile
        ? { position: "fixed", inset: 0, background: B.white, zIndex: 1000, display: "flex", flexDirection: "column" }
        : { position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" };

    const innerStyle = isMobile
        ? { display: "flex", flexDirection: "column", flex: 1, height: "100%" }
        : { background: B.white, borderRadius: 14, width: 640, maxWidth: "94vw", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,.18)" };

    return (
        <div style={overlayStyle} onClick={isMobile ? undefined : onClose}>
            <div style={innerStyle} onClick={e => e.stopPropagation()}>
                {/* Modal header – orange accent strip */}
                <div style={{ borderTop: `3px solid ${B.orange}`, borderRadius: "14px 14px 0 0" }} />
                <div style={{
                    padding: "16px 20px", borderBottom: `1px solid ${B.gray200}`,
                    display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
                }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: B.gray900, fontFamily: B.font }}>{title}</h3>
                    <button onClick={onClose} style={{ border: "none", background: B.gray100, cursor: "pointer", color: B.gray500, display: "flex", alignItems: "center", padding: 6, borderRadius: 8 }}>
                        <Icons.Close />
                    </button>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 24 }}>{children}</div>
            </div>
        </div>
    );
}

/* ─── Form Modal ─────────────────────────────────────────────────────── */
function DeliverySlotFormModal({ open, onClose, onSubmit, initial, locations }) {
    const isMobile = useIsMobile();
    const [form, setForm] = useState({ delivery_location_id: "", name: "", visible_at: "", cut_off_at: "", start_time: "", end_time: "", is_active: true });

    useEffect(() => {
        if (initial) {
            setForm({
                delivery_location_id: initial.delivery_location_id || "",
                name: initial.name || "",
                visible_at:  (initial.visible_at  || "").slice(0, 5),
                cut_off_at:  (initial.cut_off_at  || "").slice(0, 5),
                start_time:  (initial.start_time  || "").slice(0, 5),
                end_time:    (initial.end_time    || "").slice(0, 5),
                is_active: Boolean(initial.is_active),
            });
        } else {
            setForm({ delivery_location_id: "", name: "", visible_at: "", cut_off_at: "", start_time: "", end_time: "", is_active: true });
        }
    }, [initial, open]);

    const submit = () => {
        if (!form.delivery_location_id) return alert("Please select delivery location");
        if (!form.name.trim())          return alert("Please enter slot name");
        if (!form.visible_at || !form.cut_off_at || !form.start_time || !form.end_time) return alert("Please fill all time fields");
        if (!(form.visible_at < form.cut_off_at && form.start_time < form.end_time))
            return alert("Time order: Visible At < Cut Off At ≤ Start Time < End Time");
        onSubmit({ ...form, delivery_location_id: Number(form.delivery_location_id), is_active: Boolean(form.is_active) });
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose} title={initial ? "Edit Delivery Slot" : "Create Delivery Slot"}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Select label="Delivery Location" value={form.delivery_location_id} onChange={e => setForm({ ...form, delivery_location_id: e.target.value })}>
                    <option value="">Select delivery location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Select>

                <Input label="Slot Name" placeholder="e.g. SRM Evening Slot" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />

                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                    <Input type="time" label="Visible At"  value={form.visible_at}  onChange={e => setForm({ ...form, visible_at:  e.target.value })} />
                    <Input type="time" label="Cut Off At"  value={form.cut_off_at}  onChange={e => setForm({ ...form, cut_off_at:  e.target.value })} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                    <Input type="time" label="Start Time"  value={form.start_time}  onChange={e => setForm({ ...form, start_time:  e.target.value })} />
                    <Input type="time" label="End Time"    value={form.end_time}    onChange={e => setForm({ ...form, end_time:    e.target.value })} />
                </div>

                {/* Active toggle row */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 16px", borderRadius: 10,
                    background: form.is_active ? B.orangeLight : B.gray50,
                    border: `1px solid ${form.is_active ? "rgba(255,107,0,.25)" : B.gray300}`,
                }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: form.is_active ? B.orange : B.gray700, fontFamily: B.font }}>
                            {form.is_active ? "Slot Active" : "Slot Inactive"}
                        </span>
                        <span style={{ fontSize: 12, color: form.is_active ? B.orangeMid : B.gray400, fontFamily: B.font }}>
                            {form.is_active ? "Customers can see this slot when timing matches" : "This slot will not be shown to customers"}
                        </span>
                    </div>
                    <ActiveToggle active={form.is_active} onToggle={() => setForm(f => ({ ...f, is_active: !f.is_active }))} />
                </div>

                <div style={{
                    display: "flex", flexDirection: isMobile ? "column-reverse" : "row",
                    justifyContent: "flex-end", gap: 10,
                    borderTop: `1px solid ${B.gray100}`, paddingTop: 16,
                }}>
                    <Btn variant="ghost" onClick={onClose} fullWidth={isMobile}>Cancel</Btn>
                    <Btn variant="primary" onClick={submit} fullWidth={isMobile}>
                        {initial ? "Update Slot" : "Create Slot"}
                    </Btn>
                </div>
            </div>
        </Modal>
    );
}

/* ─── Empty State ────────────────────────────────────────────────────── */
function EmptyState() {
    return (
        <div style={{
            textAlign: "center", padding: "56px 20px", color: B.gray400,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
        }}>
            <div style={{ color: B.gray300 }}><Icons.EmptyBox /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: B.gray600, fontFamily: B.font }}>No delivery slots yet</div>
            <div style={{ fontSize: 13, fontFamily: B.font }}>Click "Add Delivery Slot" to create one</div>
        </div>
    );
}

/* ─── Main Page ──────────────────────────────────────────────────────── */
export default function DeliverySlots() {
    const isMobile = useIsMobile();
    const [slots, setSlots]       = useState([]);
    const [locations, setLocs]    = useState([]);
    const [loading, setLoading]   = useState(true);
    const [showCreate, setCreate] = useState(false);
    const [editTarget, setEdit]   = useState(null);
    const [togglingId, setTogId]  = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [sr, lr] = await Promise.all([getAllDeliverySlots(), fetchDeliveryLocations()]);
            if (sr.success) { const d = sr.data?.data || sr.data?.slots || sr.data || []; setSlots(Array.isArray(d) ? d : []); }
            if (lr.success) { const d = lr.data?.data || lr.data?.locations || lr.data || []; setLocs(Array.isArray(d) ? d : []); }
        } catch (e) { console.error(e); alert("Failed to load delivery slots"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (data) => {
        try {
            const res = await createDeliverySlot(data);
            if (res.success) setSlots(p => [...p, res.data?.slot || res.data]);
        } catch (e) { alert(e?.response?.data?.message || "Failed to create"); }
    };

    const handleUpdate = async (data) => {
        try {
            const res = await updateDeliverySlot(editTarget.id, data);
            if (res.success) { const u = res.data?.slot || res.data; setSlots(p => p.map(s => s.id === editTarget.id ? u : s)); }
        } catch (e) { alert(e?.response?.data?.message || "Failed to update"); }
    };

    const handleDelete = async (slot) => {
        if (!window.confirm(`Delete "${slot.name}"?`)) return;
        try {
            const res = await deleteDeliverySlot(slot.id);
            if (res.success) setSlots(p => p.filter(s => s.id !== slot.id));
        } catch (e) { alert(e?.response?.data?.message || "Failed to delete"); }
    };

    const handleToggle = async (slot) => {
        setTogId(slot.id);
        const old = slots;
        setSlots(p => p.map(s => s.id === slot.id ? { ...s, is_active: !Boolean(s.is_active) } : s));
        try {
            const res = await toggleDeliverySlotActive(slot.id);
            if (res.success) {
                const u = res.data?.slot || res.data;
                if (u?.id) setSlots(p => p.map(s => s.id === slot.id ? u : s));
            } else setSlots(old);
        } catch (e) { setSlots(old); alert(e?.response?.data?.message || "Failed to toggle"); }
        finally { setTogId(null); }
    };

    /* ── header stat badges ── */
    const activeCount   = slots.filter(s => s.is_active).length;
    const inactiveCount = slots.length - activeCount;

    return (
        <>
            <style>{`
                @keyframes ds-shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                .ds-row:hover { background: #fafbff !important; }
                .ds-row { transition: background .12s; }
            `}</style>

            <div style={{ fontFamily: B.font, background: "#f4f6f9", minHeight: "100vh" }}>
                <div style={{ padding: isMobile ? "20px 16px 80px" : "28px 28px 80px" }}>

                    {/* ── Page header ── */}
                    <div style={{ marginBottom: 24 }}>
                        {/* breadcrumb-style label */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ color: B.orange }}><Icons.Slots /></span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: B.gray500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                Orders / Delivery Slots
                            </span>
                        </div>

                        <div style={{
                            display: "flex", flexDirection: isMobile ? "column" : "row",
                            justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center",
                            gap: isMobile ? 14 : 0,
                        }}>
                            <div>
                                <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 22, color: B.gray900, fontWeight: 800 }}>
                                    Delivery Slots
                                </h1>
                                <p style={{ margin: "3px 0 0", fontSize: 13, color: B.gray500 }}>
                                    Manage customer-visible slot timings by delivery location
                                </p>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                {/* Mini stat badges (matches your dashboard .badge style) */}
                                {!loading && (
                                    <>
                                        <span style={{ background: B.orangeLight, color: B.orange, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1px solid rgba(255,107,0,.2)` }}>
                                            {activeCount} Active
                                        </span>
                                        <span style={{ background: B.gray100, color: B.gray600, padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, border: `1px solid ${B.gray300}` }}>
                                            {inactiveCount} Inactive
                                        </span>
                                    </>
                                )}
                                <Btn variant="primary" onClick={() => setCreate(true)} fullWidth={isMobile}>
                                    <Icons.Plus /> Add Delivery Slot
                                </Btn>
                            </div>
                        </div>
                    </div>

                    {/* ── Mobile cards ── */}
                    {isMobile && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {loading && [0, 1, 2].map(i => (
                                <div key={i} style={{ background: B.white, borderRadius: 12, border: `1px solid ${B.gray200}`, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                                    <Skeleton w="55%" h={16} /><Skeleton w="35%" h={12} />
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                        {[0,1,2,3].map(j => <Skeleton key={j} h={52} />)}
                                    </div>
                                </div>
                            ))}
                            {!loading && slots.length === 0 && <EmptyState />}
                            {!loading && slots.map(slot => (
                                <DeliverySlotCard key={slot.id} slot={slot} toggling={togglingId === slot.id}
                                                  onToggle={() => handleToggle(slot)} onEdit={() => setEdit(slot)} onDelete={() => handleDelete(slot)} />
                            ))}
                        </div>
                    )}

                    {/* ── Desktop table (matches .section-card / om-table patterns) ── */}
                    {!isMobile && (
                        <div style={{ background: B.white, borderRadius: 12, border: `1px solid ${B.gray200}`, boxShadow: B.shadowMd, overflow: "hidden" }}>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
                                    <thead>
                                    <tr style={{ background: B.gray50, borderBottom: `1px solid ${B.gray200}` }}>
                                        {[
                                            { label: "Slot Name" },
                                            { label: "Location", icon: <Icons.Location /> },
                                            { label: "Visible At", icon: <Icons.Clock /> },
                                            { label: "Cut Off At", icon: <Icons.Clock /> },
                                            { label: "Start Time", icon: <Icons.Clock /> },
                                            { label: "End Time", icon: <Icons.Clock /> },
                                            { label: "Status" },
                                            { label: "Updated At" },
                                            { label: "Actions" },
                                        ].map(({ label, icon }, i) => (
                                            <th key={i} style={{
                                                padding: "12px 16px", textAlign: "left",
                                                fontSize: 12, fontWeight: 700, color: B.gray600,
                                                textTransform: "uppercase", letterSpacing: "0.07em",
                                                background: B.gray50, whiteSpace: "nowrap",
                                                fontFamily: B.font,
                                            }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                    {icon && <span style={{ color: B.orange }}>{icon}</span>}
                                                    {label}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {loading && [0,1,2,3].map(i => <SkeletonRow key={i} cols={9} />)}

                                    {!loading && slots.length === 0 && (
                                        <tr><td colSpan={9}><EmptyState /></td></tr>
                                    )}

                                    {!loading && slots.map((slot, idx) => {
                                        const locName = slot.delivery_location?.name || slot.deliveryLocation?.name || slot.location?.name || "—";
                                        return (
                                            <tr key={slot.id} className="ds-row" style={{ borderBottom: idx < slots.length - 1 ? `1px solid ${B.gray100}` : "none" }}>
                                                <td style={{ padding: "14px 16px" }}>
                                                    <span style={{ fontSize: 14, fontWeight: 700, color: B.gray900, fontFamily: B.font }}>{slot.name}</span>
                                                </td>

                                                <td style={{ padding: "14px 16px" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: B.gray700, fontFamily: B.font }}>
                                                        <span style={{ color: B.orange }}><Icons.Location /></span>
                                                        {locName}
                                                    </div>
                                                </td>

                                                {[slot.visible_at, slot.cut_off_at, slot.start_time, slot.end_time].map((t, j) => (
                                                    <td key={j} style={{ padding: "14px 16px", fontSize: 13, color: B.gray700, fontFamily: B.font, whiteSpace: "nowrap" }}>
                                                        {fmt12(t)}
                                                    </td>
                                                ))}

                                                <td style={{ padding: "14px 16px" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <ActiveToggle active={Boolean(slot.is_active)} disabled={togglingId === slot.id} onToggle={() => handleToggle(slot)} />
                                                        <StatusBadge active={Boolean(slot.is_active)} />
                                                    </div>
                                                </td>

                                                <td style={{ padding: "14px 16px", fontSize: 12, color: B.gray500, whiteSpace: "nowrap", fontFamily: B.font }}>
                                                    {formatDateTime(slot.updated_at)}
                                                </td>

                                                <td style={{ padding: "14px 16px" }}>
                                                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                                        <Btn small variant="ghost" onClick={() => setEdit(slot)}>
                                                            <Icons.Settings /> Edit
                                                        </Btn>
                                                        <Btn small variant="danger" onClick={() => handleDelete(slot)}>
                                                            <Icons.Trash /> Delete
                                                        </Btn>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <DeliverySlotFormModal open={showCreate} onClose={() => setCreate(false)} onSubmit={handleCreate} locations={locations} />
                <DeliverySlotFormModal open={!!editTarget} onClose={() => setEdit(null)} onSubmit={handleUpdate} initial={editTarget} locations={locations} />
            </div>
        </>
    );
}